/**
 * Live trivia game room.
 *
 * Polls GET /live/{id}/state every 2 seconds. The server is the clock.
 *
 * Rendering strategy:
 *   - The shell (header + body container) is painted once.
 *   - Inside the body, we only re-render the whole template when the
 *     "render key" (phase + question_index + final-render-flag) changes.
 *   - For the in-between ticks (4× per second) we mutate just the timer
 *     digits + the progress bar width via direct DOM updates. This is what
 *     kills the flicker — innerHTML-replacing 4×/s wipes the answer
 *     buttons and any in-flight click animation.
 *
 * Answer flow:
 *   - Click → optimistic UI lock (highlight chosen, disable rest, render the
 *     answered/locked state) BEFORE the POST. The server confirmation is
 *     just a points toast; the UI never waits for it.
 */

import { api, ApiError } from '../api.js';
import { startCollector } from '../lib/client_signals.js';
import { escape } from '../components/form.js';
import { gameAudio, mountAudioToggle } from '../lib/game_audio.js';
import { preloadImage, questionImageHtml } from '../lib/question_image.js';
import { mountDecoyBlock, mountQuestionView } from '../lib/question_view.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { TrivianaSocket } from '../ws.js';

// Curated set — must match `ALLOWED_EMOJIS` on the backend. Kept in this
// order so the toolbar palette reads left-to-right the same way for every
// user. No letter/number emojis on purpose — reactions must not double as
// answer signals between colluding players.
const REACTION_EMOJIS = ['🔥', '😂', '😮', '👏', '💀', '🙌'];

// 1.2s poll cadence — tightened from 2s after players reported "missed
// reveal, jumped straight to next question" on 2026-07-16. With the
// old 2s poll and a (now-widened-to-5s) reveal window, jitter could
// occasionally leave a client seeing `answering(Q0)` → `answering(Q1)`
// with no reveal in between. 1.2s + 5s reveal means every reveal
// window is caught by at least 3 poll cycles. Countdown + progress
// bar still animate via TICK_MS client-side extrapolation, so this
// change only affects the phase-transition responsiveness, not the
// per-tick UI smoothness.
const POLL_MS = 1200;
const TICK_MS = 250;

export async function renderLiveRoom(root) {
  // /live/{id}/play → ['', 'live', '{id}', 'play']
  // Index 2 is the trivia id. `.pop()` would have grabbed 'play' since
  // we moved the room from /live/{id} → /live/{id}/play.
  const id = window.location.pathname.split('/').filter(Boolean)[1];

  root.innerHTML = `
    <div class="appbar" id="live-room-appbar">
      <a href="/live" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title" id="live-room-title">Live trivia</div>
      <div class="appbar-spacer"></div>
      <span class="tag" id="live-room-phase">…</span>
    </div>
    <div class="pad" id="live-room-body">
      <div class="card muted">Loading…</div>
    </div>
    <div class="rx-toolbar" id="rx-toolbar" role="group" aria-label="Reactions">
      ${REACTION_EMOJIS.map((e) => (
        `<button type="button" class="rx-btn" data-rx="${e}" aria-label="React ${e}">${e}</button>`
      )).join('')}
    </div>
    <div class="rx-layer" id="rx-layer" aria-hidden="true"></div>
  `;
  // Gameplay ambient — muted by default; tap the speaker in the appbar
  // (which is also the gesture mobile browsers require to unlock audio).
  const detachAudio = mountAudioToggle(document.getElementById('live-room-appbar'));
  gameAudio.start();

  const state = {
    id,
    lastServer: null,    // most recent server state response
    lastFetchedAt: 0,    // performance.now() when lastServer arrived
    renderKey: null,     // last full-render key — see _renderKey()
    submitting: false,
    answeredOrder: null, // per-Q: user's chosen choice_index once they pick
    answerLocked: null,  // question_index at the moment they picked
    pollTimer: null,
    tickTimer: null,
    cancelled: false,
    // 50/50 power-up local state. `eliminatedByQ[q.order] = [idx, idx]`
    // — populated when the player taps Use 50/50 OR when /state reports
    // this question is already in `your_fiftyfifty_questions` (server
    // is the source of truth, so a reconnect/refresh paints the same
    // greyed-out options without re-tapping).
    eliminatedByQ: {},
    // Counters that the local Use 50/50 button reads from. Updated on
    // every poll and immediately after a successful POST.
    fiftyfiftyRemaining: 0,
    fiftyfiftyBalance: 0,
    submittingFiftyfifty: false,
    // Anti-cheat telemetry (see lib/client_signals.js). One collector per
    // question; `collectorForOrder` tracks which question it belongs to
    // so a phase-tick repaint doesn't spuriously restart the timer.
    // Snapshot at answer submit; dispose on reveal / navigation.
    collector: null,
    collectorForOrder: null,
  };

  const reactions = wireReactions(state);
  const stopAll = () => {
    state.cancelled = true;
    if (state.pollTimer) clearTimeout(state.pollTimer);
    if (state.tickTimer) clearInterval(state.tickTimer);
    if (state.collector) {
      state.collector.dispose();
      state.collector = null;
      state.collectorForOrder = null;
    }
    reactions.stop();
    gameAudio.stop();
    detachAudio();
  };
  window.addEventListener('popstate', stopAll, { once: true });

  // Local ticking — only updates the timer/progress, never re-renders body.
  state.tickTimer = setInterval(() => tickFrame(state), TICK_MS);

  await poll(state);
}

async function poll(state) {
  if (state.cancelled) return;
  try {
    const s = await api(`/live/${state.id}/state`);
    state.lastServer = s;
    state.lastFetchedAt = performance.now();
    // Question advanced → clear the per-Q optimistic lock so the new
    // question's buttons are clickable.
    if (s.question_index !== state.answerLocked) {
      state.answerLocked = null;
      state.answeredOrder = null;
    }
    // Server-of-truth sync for 50/50 state. Counters drive the HUD;
    // `your_fiftyfifty_current_eliminations` rehydrates the dimmed-button
    // visual after a refresh / reconnect so the player doesn't lose the
    // 50/50 advantage when the page reloads mid-question.
    state.fiftyfiftyRemaining = Number(s.your_fiftyfifty_remaining || 0);
    state.fiftyfiftyBalance = Number(s.your_fiftyfifty_balance || 0);
    if (s.question_index != null
        && Array.isArray(s.your_fiftyfifty_current_eliminations)
        && s.your_fiftyfifty_current_eliminations.length > 0
        && !state.eliminatedByQ[s.question_index]) {
      state.eliminatedByQ[s.question_index] = s.your_fiftyfifty_current_eliminations.slice();
      // Force a body repaint so the dimmed wrongs appear without
      // waiting for the next renderKey change.
      state.renderKey = null;
    }
    paint(state);

    if (s.phase === 'finished' || s.phase === 'cancelled') {
      // Final paint is already done; we still poll once more in case the
      // server's leaderboard ordering changes during the wind-down. But not
      // forever — bail out after the FINAL frame.
      return;
    }
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    const body = document.getElementById('live-room-body');
    if (body) body.innerHTML = `<div class="card" style="color:var(--pink-t)">Reconnecting…</div>`;
    // Overwriting body.innerHTML above orphans whatever was there.
    // paint() only repaints when `renderKey` mismatches — if the next
    // successful poll returns the same phase/question we were in before
    // the error, the key would match and the "Reconnecting…" message
    // would stay stuck on screen. Invalidate the key so the very next
    // paint always rebuilds the body. This was the frozen-screen bug.
    state.renderKey = null;
  }
  state.pollTimer = setTimeout(() => poll(state), POLL_MS);
}

// ---- render plumbing ------------------------------------------------------

function _renderKey(s, state) {
  // Anything that requires a full template swap goes into the key.
  // The 50/50 marker is included so tapping Use 50/50 (which fills in
  // state.eliminatedByQ[order]) repaints the choice buttons with the
  // dimmed/struck wrongs.
  //
  // The two ff* flags ensure the 50/50 HUD repaints when the player's
  // inventory or per-game remaining changes mid-question — without this
  // the body keeps the stale "0 owned" version of the HUD rendered at
  // first paint even after a later poll hydrates the real balance.
  // Bug surfaced when users reported the Use 50/50 button never
  // appearing despite having 50/50s in inventory.
  const qIdx = s.question_index;
  const ffHit = qIdx != null && state.eliminatedByQ[qIdx] ? '50' : '0';
  const ffArmable = (state.fiftyfiftyBalance || 0) > 0 ? 'b' : '_';
  const ffSlotsLeft = (state.fiftyfiftyRemaining || 0) > 0 ? 'r' : '_';
  // Reveal has two sub-phases even though `s.phase` doesn't differentiate:
  // during the first _LATE_ANSWER_GRACE_MS (~750ms) the server withholds
  // `correct_index` (anti-cheat — see live.py), then exposes it for the
  // remainder of the reveal window. Without this flag in the key, the
  // first reveal poll (correct_index=null) paints "no highlighted answer"
  // and every subsequent reveal poll shares the same key so paint()
  // short-circuits — the player sits on an un-highlighted reveal for the
  // full window and jumps straight to the next question. Bug reported
  // 2026-07-23; the 2026-07-16 poll-cadence tightening (2s → 1.2s) made
  // it more frequent by raising the odds of the first reveal poll
  // landing in the grace window.
  const revealReady = s.phase === 'reveal' && s.correct_index != null ? 'r' : '_';
  return [
    s.phase,
    qIdx,
    state.answerLocked === qIdx ? 'locked' : 'open',
    revealReady,
    ffHit,
    ffArmable,
    ffSlotsLeft,
  ].join('|');
}

function paint(state) {
  if (!state.lastServer) return;
  const s = state.lastServer;

  // Update the always-visible pieces (title + phase pill) every paint.
  const titleEl = document.getElementById('live-room-title');
  if (titleEl) titleEl.textContent = s.title || 'Live trivia';
  const phaseEl = document.getElementById('live-room-phase');
  if (phaseEl) {
    phaseEl.className = `tag ${phasePillClass(s.phase)}`;
    phaseEl.textContent = phasePillLabel(s);
  }

  const key = _renderKey(s, state);
  if (key !== state.renderKey) {
    state.renderKey = key;
    const body = document.getElementById('live-room-body');
    if (!body) return;
    body.innerHTML = renderPhaseBody(s, state);
    // Prompt + option grid live inside a closed shadow root (see
    // lib/question_view.js). Every body repaint recreates the host div,
    // so we (re-)mount the view here with the current per-question
    // state. Callback-based click handling replaces the old global
    // `querySelectorAll('.live-choice')` wire — bots that inject a click
    // no longer have any handle in the light DOM to target.
    if (s.phase === 'answering' || s.phase === 'reveal') {
      mountLiveQuestionView(state, s);
    }
    if (s.phase === 'answering') {
      wireFiftyfifty(state);
    }
  }
  // Always run the in-place tick refresh immediately after a (re)paint so
  // the visible timer matches the freshest server state.
  tickFrame(state);
}

function tickFrame(state) {
  if (!state.lastServer) return;
  const s = state.lastServer;
  const drift = performance.now() - state.lastFetchedAt;
  const remaining = Math.max(0, Math.round(s.remaining_ms - drift));

  const timerEl = document.getElementById('live-timer-secs');
  if (timerEl) timerEl.textContent = secs(remaining) + 's';

  // Numeric big-digit countdown for pregame/scheduled.
  const bigEl = document.getElementById('live-timer-big');
  if (bigEl) bigEl.textContent = secs(remaining);

  // Smoothly shrink the progress bar — width transition kicks in because
  // we're updating in place rather than recreating the element.
  const bar = document.getElementById('live-progress-bar');
  if (bar && s.phase === 'answering') {
    const total = s.answer_window_ms || 15000;
    const pct = Math.max(0, Math.min(100, (remaining / total) * 100));
    bar.style.width = pct + '%';
  }
}

function renderPhaseBody(s, state) {
  switch (s.phase) {
    case 'scheduled': return scheduledHtml(s);
    case 'pregame':   return pregameHtml(s);
    case 'answering': return answeringHtml(s, state);
    case 'reveal':    return revealHtml(s);
    case 'finished':  return finishedHtml(s);
    case 'cancelled': return cancelledHtml(s);
    default:          return `<div class="card muted">${escape(s.phase || 'unknown')}</div>`;
  }
}

// ---- helpers --------------------------------------------------------------

function phasePillClass(phase) {
  if (phase === 'answering') return 'mag';
  if (phase === 'reveal') return 'vio';
  if (phase === 'finished') return 'ok';
  if (phase === 'cancelled') return 'bad';
  return 'warn';
}
function phasePillLabel(s) {
  switch (s.phase) {
    case 'scheduled': return 'WAITING';
    case 'pregame':   return 'STARTING';
    case 'answering': return `Q${(s.question_index ?? 0) + 1} / ${s.question_count}`;
    case 'reveal':    return 'REVEAL';
    case 'finished':  return 'FINAL';
    case 'cancelled': return 'CANCELLED';
    default: return s.phase;
  }
}
function secs(ms) { return Math.max(0, Math.ceil(ms / 1000)); }

// ---- per-phase paints (each returns a string of HTML) ---------------------

function headerHtml(s, sub) {
  return `
    <div class="card" style="padding:12px;">
      <div class="row" style="align-items:flex-start;">
        <div class="grow">
          <div style="font-family:var(--font-display);font-size:15px;font-weight:600;color:#fff;">
            ${escape(s.title || 'Live trivia')}
          </div>
          <div class="muted" style="font-size:11px;margin-top:2px;">
            ${s.participants_count} ${s.participants_count === 1 ? 'player' : 'players'} ·
            ${s.question_count} questions
          </div>
        </div>
        ${sub ? `<div style="text-align:right;font-family:var(--font-display);">${sub}</div>` : ''}
      </div>
    </div>
  `;
}

/**
 * Eraser HUD — gold pill rendered under the header during play, showing
 * whether the player brought any erasers and how many remain. Two states:
 *   - remaining > 0 → "ERASER ARMED · N LEFT" with a short explanation
 *   - remaining == 0 (used or all consumed) → "ERASER USED" — softer,
 *     non-action styling so it reads as a spent-resource state.
 * Renders nothing for players who didn't bring any to this game.
 */
function eraserHudHtml(s) {
  const brought = Number(s.your_erasers_brought || 0);
  if (brought <= 0) return '';
  const used = Number(s.your_erasers_used || 0);
  const remaining = Math.max(0, brought - used);

  if (remaining > 0) {
    return `
      <div class="row" style="margin:8px 2px 0;align-items:center;gap:10px;">
        <span style="display:inline-flex;align-items:center;gap:6px;padding:6px 10px;background:rgba(229,193,0,0.12);border:1px solid rgba(229,193,0,0.5);border-radius:8px;font-family:var(--font-display);font-size:11px;font-weight:700;color:#E5C100;letter-spacing:0.08em;text-transform:uppercase;">
          ✏️ Eraser armed · ${remaining} left
        </span>
        <span class="muted" style="font-size:11.5px;">
          corrects your first miss
        </span>
      </div>
    `;
  }
  return `
    <div class="row" style="margin:8px 2px 0;align-items:center;gap:10px;">
      <span style="display:inline-flex;align-items:center;gap:6px;padding:6px 10px;background:rgba(255,255,255,0.04);border:1px solid #2A2244;border-radius:8px;font-family:var(--font-display);font-size:11px;font-weight:700;color:var(--mute);letter-spacing:0.08em;text-transform:uppercase;">
        ✏️ Eraser used
      </span>
      <span class="muted" style="font-size:11.5px;">
        no auto-correct left
      </span>
    </div>
  `;
}

function scheduledHtml(s) {
  const sub = `
    <div style="font-size:11px;color:var(--mute);">starts in</div>
    <div id="live-timer-big" style="font-size:22px;color:#fff;">${secs(s.remaining_ms)}</div>
  `;
  return `
    ${headerHtml(s, sub)}
    <div class="card muted">Hold tight — the host will start this trivia shortly.</div>
  `;
}

function pregameHtml(s) {
  return `
    ${headerHtml(s)}
    ${eraserHudHtml(s)}
    <div class="hero violet center" style="padding:36px 16px;">
      <div style="font-family:var(--font-display);font-size:13px;letter-spacing:6px;opacity:0.85;">GET READY</div>
      <div id="live-timer-big" style="font-size:64px;font-family:var(--font-display);margin:14px 0;color:#fff;font-weight:700;">${secs(s.remaining_ms)}</div>
      <div class="muted" style="font-size:12px;">First question coming up…</div>
    </div>
  `;
}

function answeringHtml(s, state) {
  const q = s.question;
  if (!q) return `${headerHtml(s)}<div class="card muted">Loading question…</div>`;

  const total = s.answer_window_ms || 15000;
  const startPct = Math.max(0, Math.min(100, (s.remaining_ms / total) * 100));
  const locked = state.answerLocked === q.order;
  // 50/50 already-used state comes from the server's per-user question set
  // (survives refresh); the specific eliminated indices are pushed into the
  // shared shadow view by mountLiveQuestionView() rather than being
  // splatted into HTML here.
  const alreadyUsed = (s.your_fiftyfifty_questions || []).includes(q.order);

  return `
    ${headerHtml(s)}
    ${eraserHudHtml(s)}
    ${fiftyfiftyHudHtml(s, state, { alreadyUsed })}
    <div class="card" style="padding:14px;">
      <div class="row">
        <span class="muted" style="font-size:11px;">Question ${q.order + 1} of ${s.question_count}</span>
        <div class="sp"></div>
        <span id="live-timer-secs" style="font-family:var(--font-display);font-size:13px;color:#fff;">${secs(s.remaining_ms)}s</span>
      </div>
      <div style="height:4px;background:#1a1432;border-radius:3px;margin:8px 0 12px;overflow:hidden;">
        <div id="live-progress-bar"
             style="height:100%;width:${startPct}%;background:linear-gradient(90deg,#5B2C91,#E94BA2);transition:width ${TICK_MS}ms linear;"></div>
      </div>
      ${questionImageHtml(q.image_url)}
      <!-- Prompt + option grid render inside a closed shadow root — see
           lib/question_view.js. mountLiveQuestionView() below re-mounts
           the view against this host after each body innerHTML swap and
           pushes the current locked / eliminated / reveal state into it.
           The qv-slot wrapper is position:relative so the decoy trap
           (mountDecoyBlock) can position its light-DOM decoys absolutely
           on top of the shadow host without affecting layout — see PR 4
           notes in lib/question_view.js. -->
      <div class="qv-slot" style="position:relative;">
        <div id="live-qv-host" style="position:relative;z-index:1;"></div>
      </div>
      ${locked
        ? `<div class="muted" style="font-size:11px;margin-top:10px;text-align:center;">Answer locked — waiting for reveal…</div>`
        : ''}
    </div>
    ${miniLeaderboardHtml(s)}
  `;
}

/**
 * 50/50 button + status pill rendered above the question card during the
 * answering phase. The button is enabled only when:
 *   - the player is a participant,
 *   - this trivia allows 50/50 (max_fiftyfifty_per_player > 0),
 *   - they have >0 remaining uses AND >0 inventory,
 *   - they haven't answered this question yet,
 *   - they haven't already used 50/50 on this question.
 * Renders nothing for quizzes that have 50/50 disabled OR for players
 * who have no inventory + no remaining uses (so the HUD doesn't crowd
 * the question for the majority of players who don't have any).
 */
function fiftyfiftyHudHtml(s, state, { alreadyUsed }) {
  const quizMax = Number(s.max_fiftyfifty_per_player || 0);
  if (quizMax <= 0) return '';
  const remaining = Math.max(0, Number(state.fiftyfiftyRemaining || 0));
  const balance = Math.max(0, Number(state.fiftyfiftyBalance || 0));
  const answered = state.answerLocked === s.question_index;
  const canTap = !alreadyUsed && !answered && remaining > 0 && balance > 0
    && !state.submittingFiftyfifty;

  if (alreadyUsed) {
    return `
      <div class="row" style="margin:8px 2px 0;align-items:center;gap:10px;">
        <span style="display:inline-flex;align-items:center;gap:6px;padding:6px 10px;background:rgba(233,75,162,0.10);border:1px solid rgba(233,75,162,0.4);border-radius:8px;font-family:var(--font-display);font-size:11px;font-weight:700;color:#E94BA2;letter-spacing:0.08em;text-transform:uppercase;">
          🎯 50/50 used
        </span>
        <span class="muted" style="font-size:11.5px;">
          two wrong answers removed
        </span>
      </div>
    `;
  }
  if (remaining <= 0 && balance <= 0) return '';

  const lblHint = balance <= 0
    ? 'no 50/50s in inventory'
    : remaining <= 0
      ? `used all ${quizMax} this game`
      : `${remaining}/${quizMax} left this game · ${balance} owned`;
  return `
    <div class="row" style="margin:8px 2px 0;align-items:center;gap:10px;">
      <button type="button" id="ff-use-btn" class="btn"
              ${canTap ? '' : 'disabled'}
              style="padding:6px 12px;font-size:12px;font-family:var(--font-display);
                     background:${canTap ? 'rgba(233,75,162,0.18)' : 'rgba(255,255,255,0.04)'};
                     border:1px solid ${canTap ? 'rgba(233,75,162,0.55)' : '#2A2244'};
                     color:${canTap ? '#fff' : 'var(--mute)'};
                     letter-spacing:0.06em;text-transform:uppercase;font-weight:700;">
        🎯 Use 50/50
      </button>
      <span class="muted" style="font-size:11.5px;">
        ${escape(lblHint)}
      </span>
    </div>
  `;
}

function revealHtml(s) {
  const q = s.question;
  if (!q) return `${headerHtml(s)}<div class="card muted">Reveal…</div>`;
  const yours = s.your_answer_index;
  const youGot = s.your_points_this_q;
  return `
    ${headerHtml(s)}
    ${eraserHudHtml(s)}
    <div class="card" style="padding:14px;">
      <div class="row">
        <span class="muted" style="font-size:11px;">Reveal · next in</span>
        <div class="sp"></div>
        <span id="live-timer-secs" style="font-family:var(--font-display);font-size:13px;color:#fff;">${secs(s.remaining_ms)}s</span>
      </div>
      ${questionImageHtml(q.image_url)}
      <!-- Reveal prompt + choices (with correct-green and your-pink
           highlights) render inside the shared closed-shadow view. See
           mountLiveQuestionView. No decoy trap on reveal — the bot has
           already committed its answer by this phase. -->
      <div class="qv-slot" style="position:relative;">
        <div id="live-qv-host" style="position:relative;z-index:1;"></div>
      </div>
      ${yours == null
        ? `<div class="muted" style="font-size:12px;margin-top:12px;">No answer this round.</div>`
        : `<div style="margin-top:12px;font-family:var(--font-display);font-size:13px;color:#fff;">
             +${youGot || 0} this question · ${s.your_total_points} total
           </div>`}
    </div>
    ${miniLeaderboardHtml(s)}
  `;
}

function finishedHtml(s) {
  return `
    ${headerHtml(s)}
    <div class="hero gold center" style="padding:30px 16px;">
      <div style="font-family:var(--font-display);font-size:13px;letter-spacing:5px;">FINAL</div>
      <div style="font-family:var(--font-display);font-size:32px;color:#fff;font-weight:700;margin-top:8px;">
        ${s.your_total_points} pts
      </div>
      <div class="muted" style="font-size:12px;">your final score</div>
    </div>
    ${leaderboardHtml(s, { full: true })}
    <a href="/live/${escape(s.id)}/results" data-link class="btn">View full results</a>
    <a href="/live" data-link class="btn ghost">Back to live trivia</a>
  `;
}

function cancelledHtml(s) {
  return `
    ${headerHtml(s)}
    <div class="card" style="color:var(--pink-t);">This trivia was cancelled by the host.</div>
    <a href="/live" data-link class="btn ghost">Back to live trivia</a>
  `;
}

function miniLeaderboardHtml(s) {
  const top = (s.leaderboard || []).slice(0, 3);
  if (top.length === 0) return '';
  return `
    <div class="card" style="padding:10px 12px;margin-top:10px;">
      <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:6px;">
        Leaderboard
      </div>
      ${top.map((r, i) => leaderRow(r, i)).join('')}
    </div>
  `;
}

function leaderboardHtml(s, { full } = {}) {
  const rows = full ? (s.leaderboard || []) : (s.leaderboard || []).slice(0, 5);
  if (rows.length === 0) return `<div class="card muted">No scores yet.</div>`;
  return `
    <div class="card" style="padding:12px;">
      <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:8px;">
        Leaderboard
      </div>
      ${rows.map((r, i) => leaderRow(r, i)).join('')}
    </div>
  `;
}

function leaderRow(r, idx) {
  // Username comes pre-resolved from the API now (falls back to email
  // local-part on the server side). Last-ditch fallback to a short id
  // suffix so we never render a raw UUID.
  const name = r.username || (r.user_id || '').slice(0, 8);
  return `
    <div class="row" style="padding:5px 0;">
      <span style="font-family:var(--font-display);font-size:12px;width:22px;color:var(--mute);">${idx + 1}</span>
      <span style="font-size:13px;color:#fff;flex:1;">${escape(name)}</span>
      <span style="font-family:var(--font-display);font-size:13px;color:#FFE9A6;">${r.points}</span>
    </div>
  `;
}

/**
 * (Re-)mount the shared closed-shadow question view against the freshly
 * rendered `#live-qv-host` div and push the current per-question state
 * into it. Called after each body innerHTML swap when the phase is
 * `answering` or `reveal`.
 *
 * The click path used to be `document.querySelectorAll('.live-choice')`,
 * which meant any userscript with page-context DOM access could grab
 * the same handles and either read their text or synthesise a click.
 * That surface is gone — the option buttons live inside a closed
 * shadow root now, and clicks come through the view's `onSelect`
 * callback below.
 */
function mountLiveQuestionView(state, s) {
  const host = document.getElementById('live-qv-host');
  if (!host) return;
  const q = s.question;
  if (!q) return;

  // Anti-cheat collector lifecycle:
  //   - Enter a new question in `answering` → dispose any leftover, start
  //     a fresh one anchored to `performance.now()`.
  //   - Enter `reveal` (or the question changes) → dispose the current
  //     one so its listeners don't outlive the question.
  //   - Answer submit consumes the collector via `state.collector.snapshot`.
  if (s.phase === 'answering') {
    if (state.collectorForOrder !== q.order) {
      if (state.collector) state.collector.dispose();
      state.collector = startCollector({ questionRenderedAt: performance.now() });
      state.collectorForOrder = q.order;
    }
  } else if (state.collector) {
    state.collector.dispose();
    state.collector = null;
    state.collectorForOrder = null;
  }

  const view = mountQuestionView(host, {
    mode: 'live',
    onSelect: (idx, event) => submitLiveAnswer(state, idx, event),
  });
  view.setQuestion({
    prompt: q.prompt,
    choices: q.choices,
    twoColumnLayout: !!q.image_url,
  });

  if (s.phase === 'reveal') {
    view.showReveal({
      correctIndex: Number.isFinite(s.correct_index) ? s.correct_index : null,
      yourIndex: Number.isFinite(s.your_answer_index) ? s.your_answer_index : null,
    });
    return;
  }

  // Answering phase.
  const eliminated = state.eliminatedByQ[q.order] || [];
  if (eliminated.length) view.setEliminated(eliminated);
  if (state.answerLocked === q.order) {
    view.lockAnswer(state.answeredOrder);
  }

  // Decoy trap — light-DOM siblings inside the qv-slot wrapper.
  // Mounted after the shadow view so it stacks correctly (real content
  // z-index:1 above decoy z-index:0). Skipped once the player has
  // locked their answer: their click already went through the real
  // shadow-DOM handler, so any subsequent decoy click would just be
  // noise. Body innerHTML swaps GC the previous decoy block along with
  // the qv-slot wrapper, so we don't need to dispose explicitly.
  if (state.answerLocked !== q.order) {
    const slot = host.parentElement;
    if (slot && state.collector) {
      mountDecoyBlock(slot, {
        onDecoyHit: () => {
          if (state.collector) state.collector.markDecoyHit();
        },
      });
    }
  }
}

/**
 * Submit a live-trivia answer for the given displayed-choice index.
 * Extracted from the old `wireAnswers()` inline handler so the shared
 * question view can fire it from its `onSelect` callback (see
 * `mountLiveQuestionView`).
 *
 * Preserves every behaviour of the previous implementation:
 *   - Optimistic lock + immediate repaint (the eventual server confirm
 *     is only a points toast; the UI never blocks on it).
 *   - Join-recovery on 403 not_participant (the anna 2026-06-15 bug —
 *     keep the guard, keep the flavour toast).
 *   - Eraser / 50-50 bonus / plain-correct toasts on success.
 *   - Late-race handling: keep the pick locked on `not_answering` /
 *     `wrong_question` so the player doesn't see their answer erased
 *     during the answering→reveal transition.
 */
async function submitLiveAnswer(state, idx, event) {
  if (state.submitting) return;
  const s = state.lastServer;
  if (!s || s.phase !== 'answering' || s.question_index == null) return;

  state.submitting = true;
  state.answeredOrder = idx;
  state.answerLocked = s.question_index;
  // Anti-cheat telemetry snapshot — see lib/client_signals.js. Captured
  // BEFORE the optimistic-lock repaint, because paint() disposes the
  // collector when it swaps into the locked template (renderKey changes)
  // and we want the pre-snapshot values (visibility_changes,
  // overlay_detected, etc.) as they stood at the moment of click.
  // Server persists the blob to live_trivia_answers.client_signals
  // verbatim for admin review; it does not affect scoring.
  const clientSignals = state.collector
    ? state.collector.snapshot({ answerEvent: event })
    : null;
  state.collector = null;
  state.collectorForOrder = null;
  state.renderKey = null;  // force the next paint() to swap templates
  paint(state);

  try {
    const res = await postAnswerWithJoinRecovery(state, {
      question_order: s.question_index,
      choice_index: idx,
      client_signals: clientSignals,
    });
    if (res.eraser_applied) {
      const remaining = Number(res.erasers_remaining || 0);
      const tail = remaining > 0
        ? ` · ${remaining} eraser${remaining === 1 ? '' : 's'} left`
        : '';
      toast.info(`✏️ Wrong answer — eraser auto-corrected!${tail}`);
    } else if (res.correct && res.fiftyfifty_bonus_applied) {
      toast.success(`+${res.points} pts · 🎯 +200 50/50 bonus`);
    } else if (res.correct) {
      toast.success(`+${res.points} pts`);
    }
  } catch (err) {
    const raced = err instanceof ApiError
      && (err.code === 'not_answering' || err.code === 'wrong_question');
    if (raced) {
      toast.error('Sent too late — try tapping a moment earlier.');
    } else {
      state.answeredOrder = null;
      state.answerLocked = null;
      state.renderKey = null;
      paint(state);
      toast.error(err.message || 'Could not submit answer.');
    }
  } finally {
    state.submitting = false;
  }
}

/**
 * Wire the Use 50/50 button (rendered by fiftyfiftyHudHtml). One-shot
 * per question — after a successful POST we stash the eliminated
 * displayed indices into state.eliminatedByQ[order] and repaint so the
 * two wrongs render in their dimmed/struck state.
 */
function wireFiftyfifty(state) {
  const btn = document.getElementById('ff-use-btn');
  if (!btn || btn.disabled) return;
  btn.addEventListener('click', async () => {
    if (state.submittingFiftyfifty) return;
    const s = state.lastServer;
    if (!s || s.phase !== 'answering' || s.question_index == null) return;
    // Block tap if the user has already locked an answer for this Q
    // (the server would 409 with already_answered, but bouncing here
    // is faster and avoids consuming a network round-trip).
    if (state.answerLocked === s.question_index) {
      toast.error("Can't use 50/50 after answering.");
      return;
    }
    state.submittingFiftyfifty = true;
    btn.disabled = true;
    try {
      const res = await api(`/live/${state.id}/fiftyfifty`, {
        method: 'POST',
        body: { question_order: s.question_index },
      });
      // Server picked the two wrong displayed indices to remove.
      state.eliminatedByQ[s.question_index] = Array.isArray(res.eliminated_indices)
        ? res.eliminated_indices.slice()
        : [];
      state.fiftyfiftyRemaining = Number(res.fiftyfifty_remaining || 0);
      state.fiftyfiftyBalance = Number(res.fiftyfifty_balance || 0);
      // Force a body repaint so the dimmed wrongs + updated HUD render.
      state.renderKey = null;
      paint(state);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not use 50/50.';
      toast.error(msg);
      btn.disabled = false;
    } finally {
      state.submittingFiftyfifty = false;
    }
  });
}

/**
 * Submit an answer, recovering from the "not_participant" 403 case.
 *
 * Backstory: on 2026-06-15 the API hit 100% CPU at trivia go-live and
 * some users' POST /live/{id}/join timed out client-side while their
 * state polls (cheaper, partly cached) kept succeeding. The user saw
 * questions appear on screen, tapped answers, and every POST /answer
 * 403'd as "not_participant" because they had no participant row.
 * The original handler swallowed those 403s as a generic toast, so
 * users thought they were playing — and ended up missing from the
 * final leaderboard (anna being one of them).
 *
 * The guard: if a single 403 "not_participant" comes back, fire a
 * one-shot /join, then retry the answer. If the recovery join itself
 * fails the user gets an explicit "Couldn't enter this trivia" toast
 * so they at least know something is wrong.
 */
async function postAnswerWithJoinRecovery(state, body) {
  try {
    return await api(`/live/${state.id}/answer`, { method: 'POST', body });
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 403 || err.code !== 'not_participant') {
      throw err;
    }
    // Recovery join — if this succeeds we retry the answer once.
    try {
      await api(`/live/${state.id}/join`, { method: 'POST' });
    } catch (joinErr) {
      throw new Error("Couldn't enter this trivia — refresh the page.");
    }
    return await api(`/live/${state.id}/answer`, { method: 'POST', body });
  }
}

/**
 * Wire the emoji-reactions palette + live socket.
 *
 * Two things happening here:
 *   1) The toolbar (rendered once in the shell so it survives phase
 *      re-renders) sends `{type:"reaction", emoji}` over /ws/live/{id}/reactions.
 *      We also spawn a local float immediately so the sender doesn't
 *      wait for the round-trip — the server's own fan-out is anonymous
 *      and won't come back with sender info, so double-spawning-from-server
 *      would just look like two of the same reaction (which is fine but
 *      makes the "did my tap register" feedback worse).
 *   2) Incoming server messages spawn a floating emoji in the fixed
 *      overlay layer. Overlay lives outside the body so it survives
 *      the innerHTML swaps that happen on every phase change.
 *
 * Client-side self-throttle mirrors the server's 2-per-second limit —
 * cheaper than sending taps we know will be dropped. The server is still
 * authoritative; the client cap is UX.
 */
function wireReactions(state) {
  const layer = document.getElementById('rx-layer');
  const toolbar = document.getElementById('rx-toolbar');
  const ws = new TrivianaSocket(`/ws/live/${encodeURIComponent(state.id)}/reactions`);
  let closed = false;
  let lastSendMs = 0;

  const spawn = (emoji) => {
    if (!layer) return;
    const el = document.createElement('div');
    el.className = 'rx-float';
    el.textContent = emoji;
    // Small horizontal jitter so reactions don't stack into a single
    // vertical column when several fire at once.
    el.style.right = `${8 + Math.random() * 40}px`;
    layer.appendChild(el);
    // Match the animation-duration in base.css.
    setTimeout(() => el.remove(), 2400);
  };

  const send = (emoji) => {
    if (!REACTION_EMOJIS.includes(emoji)) return;
    const now = performance.now();
    // 500ms self-throttle — server allows 2/s per (user, live); keeping
    // the client just under that avoids visibly-dropped taps.
    if (now - lastSendMs < 500) return;
    lastSendMs = now;
    // Don't spawn locally — the server echoes every reaction to every
    // subscriber (including the sender), so a local spawn would render
    // the same emoji twice. The button's rx-pulse is the immediate ack.
    ws.send({ type: 'reaction', emoji });
  };

  if (toolbar) {
    toolbar.addEventListener('click', (ev) => {
      const btn = ev.target && ev.target.closest && ev.target.closest('.rx-btn');
      if (!btn) return;
      const emoji = btn.getAttribute('data-rx');
      btn.classList.remove('rx-pulse');
      // Force reflow so the animation restarts on repeat taps.
      // eslint-disable-next-line no-unused-expressions
      btn.offsetWidth;
      btn.classList.add('rx-pulse');
      send(emoji);
    });
  }

  ws.on('message', (m) => {
    if (!m || typeof m !== 'object') return;
    if (m.type === 'reaction' && typeof m.emoji === 'string') {
      spawn(m.emoji);
    }
  });
  ws.connect();

  return {
    stop() {
      if (closed) return;
      closed = true;
      ws.close();
    },
  };
}
